/* ==========================================================================
   WAYPOINT — the "More" sheet behind the fifth tab.

   A bottom nav's job is to be glanceable, and six icons stop being a map and
   start being a search. But the tabs were never peers to begin with: Now,
   Trips, Log and Map are places you go repeatedly while planning or driving;
   Scooty, Settings and Blog are places you go occasionally and deliberately.
   Only the first kind earns a permanent slot. (Scooty held the third slot
   until 2026-09-05; it swapped with the travel-day screen because the chat is
   used rarely and the windshield is used at 60mph. Its unread badge moved to
   the More tab, and the row here carries the count too.)

   The bonus is what this fixes on the way. Memories, Journal and Stays were
   already deep-link-only with no entry point anywhere in the app — reachable
   only by typing a URL — which is exactly the bug that made the blog invisible
   the first time someone went looking for it. Four destinations get an address
   here instead of one icon getting added to the tray.

   The trade, stated rather than hidden: Settings and Journal each cost one extra
   tap now. That is the price of the four frequent destinations staying legible.

   2026-09-06 (docs/JOURNAL-BLOG-MEMORIES.md §2): this tray used to say "Blog"
   and go to the DRAFTS list, while the actual blog — public, at a real URL —
   had no door anywhere in the app. Two different things wearing one name, and
   the one a stranger can read was the one you could not reach. Now: "Journal"
   is where we write (drafts and published together, one row per entry), and
   "Public blog" is the site itself, shown with its address and opened in a new
   tab because /blog is server-rendered HTML, not an SPA route — navigating in
   place would tear the app down. The retired nightly write-ups gave up the
   "Journal" name and moved to /journal/archive, linked from the foot of the
   Journal screen; a graveyard does not deserve a row in the tray.
   ========================================================================== */

import { motion } from 'framer-motion';
import { navigate } from '../lib/router';
import { tick } from '../lib/haptics';
import { useStoreSelector } from '../lib/store';
import { useModalSheet, SheetGrabber, SHEET_GRABBER_CSS } from './useModalSheet';
import { BlogIcon, BucketListIcon, MemoriesIcon, LogIcon, ScootyIcon, SettingsIcon, ChevronIcon } from './icons';

interface MoreItem {
  path: string;
  label: string;
  hint: string;
  icon: typeof BlogIcon;
  /** Leaves the SPA. `path` is a real server URL and the row opens it in a new tab rather than
   *  calling navigate(), which would hand a server-rendered page to the client router. */
  external?: boolean;
}

/** Ordered by how often someone actually reaches for them, not alphabetically
 *  and not by when they were built. Scooty leads because it is the one with
 *  somebody waiting on the other end; the bucket list next because it is the
 *  one consulted WHILE planning — the others are read after the fact. */
export const MORE_ITEMS: MoreItem[] = [
  {
    path: '/scooty',
    label: 'Scooty',
    hint: 'The shared chat: research, proposals, notes for each other',
    icon: ScootyIcon,
  },
  {
    path: '/bucket-list',
    label: 'Bucket list',
    hint: 'Places to see some day, matched to your routes',
    icon: BucketListIcon,
  },
  {
    path: '/journal',
    label: 'Journal',
    hint: "What we've written — drafts, and what is public",
    icon: LogIcon,
  },
  {
    path: '/memories',
    label: 'Memories',
    hint: 'Every photo and note, newest first',
    icon: MemoriesIcon,
  },
  {
    path: '/blog',
    label: 'Public blog',
    hint: 'The site everyone else sees — opens in a new tab',
    icon: BlogIcon,
    external: true,
  },
  {
    path: '/settings',
    label: 'Settings',
    hint: 'Appearance and switches',
    icon: SettingsIcon,
  },
];

/** 'waypoint.oakleafdigital.net/blog' — host + path, no scheme, the way someone would read it
 *  aloud. Origin comes from the browser, so this is right on the VPS and on localhost alike with
 *  nothing to configure. */
function publicUrlLabel(path: string): string {
  if (typeof window === 'undefined') return path;
  return `${window.location.host}${path}`;
}

export function MoreSheet({ onClose }: { onClose: () => void }) {
  const { sheetRef, reduceMotion, dragControls, sheetMotion } = useModalSheet(onClose);
  const chatUnread = useStoreSelector((s) => s.chatUnread);
  const totalUnread = chatUnread.reduce((sum, t) => sum + t.unread, 0);

  function go(item: MoreItem) {
    tick();
    if (item.external) {
      // Same origin, but a different application: server-rendered HTML from routes/blog.ts. A new
      // tab keeps the app (and whatever was half-written in it) exactly where it was.
      onClose();
      window.open(item.path, '_blank', 'noopener');
      return;
    }
    // Close first, then navigate: leaving the sheet mounted across the route
    // change would restore focus (useModalSheet's cleanup) onto the More tab of
    // a screen that has already been replaced.
    onClose();
    navigate(item.path);
  }

  return (
    <div className="moresheet-root">
      <motion.button
        type="button"
        className="moresheet-backdrop"
        aria-label="Close"
        onClick={onClose}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: reduceMotion ? 0.15 : 0.22 }}
      />
      <motion.div
        ref={sheetRef}
        className="moresheet card"
        role="dialog"
        aria-modal="true"
        aria-label="More"
        {...sheetMotion}
      >
        <SheetGrabber dragControls={dragControls} />
        <h2 className="text-title moresheet__title">More</h2>
        <ul className="moresheet__list">
          {MORE_ITEMS.map((item) => {
            const Icon = item.icon;
            return (
              <li key={item.path}>
                <button type="button" className="moresheet__item" onClick={() => go(item)}>
                  <span className="moresheet__icon" aria-hidden="true">
                    <Icon size={22} />
                  </span>
                  <span className="moresheet__text">
                    <span className="moresheet__label">
                      {item.label}
                      {item.path === '/scooty' && totalUnread > 0 && (
                        <span className="moresheet__badge" aria-label={`${totalUnread} unread`}>
                          {totalUnread > 9 ? '9+' : totalUnread}
                        </span>
                      )}
                    </span>
                    <span className="text-caption moresheet__hint">{item.hint}</span>
                    {/* "Where is the public blog via URL?" — Jeff. Answered in the row that goes
                        there, not only on the screen you reach afterwards. */}
                    {item.external && (
                      <span className="text-caption moresheet__url">{publicUrlLabel(item.path)}</span>
                    )}
                  </span>
                  <ChevronIcon size={18} className="moresheet__chev" />
                </button>
              </li>
            );
          })}
        </ul>
      </motion.div>

      <style>{`
        ${SHEET_GRABBER_CSS}
        .moresheet-root { position: fixed; inset: 0; z-index: 60; }
        .moresheet-backdrop {
          position: absolute; inset: 0;
          width: 100%; height: 100%;
          background: rgba(14, 23, 20, .38);
        }
        .moresheet {
          position: absolute; left: 0; right: 0; bottom: 0;
          margin: 0 auto;
          max-width: 520px;
          padding: 10px 16px calc(18px + var(--sab));
          border-radius: var(--radius-sheet) var(--radius-sheet) 0 0;
          background: var(--canvas-raised);
          box-shadow: var(--shadow-float);
        }
        .moresheet__title { margin: 6px 4px 10px; }
        .moresheet__list { display: flex; flex-direction: column; gap: 4px; }
        .moresheet__item {
          display: flex; align-items: center; gap: 14px;
          width: 100%;
          /* 60px, comfortably past the 44px minimum — this is a one-thumb list
             reached at the bottom of the screen, where accuracy is worst. */
          min-height: 60px;
          padding: 0 8px;
          border-radius: var(--radius-card);
          text-align: left;
        }
        .moresheet__item:active { background: var(--canvas); }
        .moresheet__icon { display: flex; color: var(--pine); }
        .moresheet__text { display: flex; flex-direction: column; gap: 2px; flex: 1; min-width: 0; }
        .moresheet__label { font-weight: 600; display: inline-flex; align-items: center; gap: 8px; }
        .moresheet__badge {
          display: inline-flex; align-items: center; justify-content: center;
          min-width: 18px; height: 18px; padding: 0 5px;
          border-radius: var(--radius-pill);
          background: var(--ember); color: #fff;
          font-size: 11px; font-weight: 700; line-height: 1;
        }
        .moresheet__hint { color: var(--ink-soft); }
        .moresheet__url {
          color: var(--pine);
          font-weight: 600;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .moresheet__chev { color: var(--ink-soft); opacity: .5; flex-shrink: 0; }
      `}</style>
    </div>
  );
}
